import { useEffect, useState } from 'react'
import type { CurrentUser } from '@/auth/permissions'
import { request } from '@/lib/api'
import { canOpenPath, NAVIGATION } from './navigation'

/**
 * Глобальный поиск для палитры Ctrl+K (тикет #127).
 *
 * Что показать, решает сервер — областью видимости списков, по живой записи. Клиент только
 * превращает найденное в путь карточки: путь берётся из той же навигации, что меню, и
 * проверяется тем же `canOpenPath`, поэтому палитра не предложит карточку раздела, которого у
 * пользователя нет (сервер её и не отдаст — это вторая линия, а не единственная).
 */

export interface SearchHit {
  /** Что найдено: ключ каталога списков (`billing.invoice`), `comment` или `email`. */
  type: string
  id: string
  title: string
  subtitle?: string | null
  fragment?: string | null
  /** Состояние записи на сейчас: `overdue` у счёта со сроком в прошлом и т. п. */
  state?: string | null
  /** Карточка, которую открыть: сама запись или та, к которой комментарий или письмо. */
  link: { entity: string; id: string }
  matchedBy?: 'text' | 'vector' | 'both'
}

export interface SearchResponse {
  query: string
  mode: 'text' | 'hybrid'
  /** Почему поиск не по смыслу — словами; нет поля — значит и не нужно было. */
  semantic?: string | null
  items: SearchHit[]
}

/** Короче двух знаков поиск по всей CRM — это выдача всего подряд. */
export const MIN_SEARCH_LENGTH = 2

/** Пауза после последней клавиши: запрос на каждую букву — нагрузка без пользы. */
export const SEARCH_DEBOUNCE_MS = 300

export function searchCrm(query: string, signal?: AbortSignal): Promise<SearchResponse> {
  return request<SearchResponse>(`/search?q=${encodeURIComponent(query)}`, { signal })
}

/** Путь карточки найденного; `null` — у пользователя нет такого раздела. */
export function hitPath(hit: SearchHit, user: CurrentUser | null): string | null {
  const item = NAVIGATION.flatMap((section) => section.items)
    .find((candidate) => candidate.listEntity === hit.link.entity)
  if (!item || !canOpenPath(user, item.path)) return null
  return `${item.path}/${hit.link.id}`
}

/** Значение, которое меняется не чаще, чем раз в `delay` мс тишины. */
export function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return settled
}
